// Lazy mixpanel facade. mixpanel-browser is ~170KiB gzipped; importing it
// statically puts that in main.js for every visitor, including local dev and
// deployments where analytics is disabled (the "dummy" token). This facade
// only downloads + initializes the SDK when a real token is configured, and
// queues calls made before the SDK has finished loading.
import config from "@/config";

type Mixpanel = typeof import("mixpanel-browser").default;

const enabled = (config.mixpanel.token ?? "dummy") !== "dummy";

let client: Mixpanel | null = null;
let loading: Promise<void> | null = null;
const queued: Array<(mp: Mixpanel) => void> = [];

function load(): Promise<void> {
    loading ??= import("mixpanel-browser").then(({ default: mp }) => {
        mp.init(config.mixpanel.token, {
            debug: false,
            // This honors the Do Not Track setting in the browser. Need to set this to false for GDPR compliance.
            ignore_dnt: false,
            // This is the EU server. We need to use this for GDPR compliance.
            api_host: "https://api-eu.mixpanel.com",
            record_sessions_percent: config.mixpanel.record_sessions_percent,
            // Completely hide elements marked with this class from recordings.
            // Used for elements like TOTP secret keys that are displayed as plaintext.
            record_block_selector: ".sensitive",
            // Note: network recording wraps window.fetch at init time. Because
            // this facade never initializes the SDK without a real token, the
            // old wrapped-fetch-breaks-POSTs failure mode (see git history of
            // main.ts) cannot occur in disabled environments.
            record_network: config.mixpanel.record_network,
            record_network_options: {
                // Capture requests that occurred before recording started
                recordInitialRequests: true,
            },
        });
        client = mp;
        for (const call of queued.splice(0)) {
            call(mp);
        }
    });
    return loading;
}

function call(fn: (mp: Mixpanel) => void): void {
    if (!enabled) {
        return;
    }
    if (client) {
        fn(client);
        return;
    }
    queued.push(fn);
    void load();
}

export default {
    trackPageview(properties: Record<string, unknown>): void {
        call((mp) => mp.track_pageview(properties));
    },
    track(name: string, properties?: Record<string, unknown>): void {
        call((mp) => mp.track(name, properties));
    },
    identify(id?: string): void {
        call((mp) => mp.identify(id));
    },
    peopleSetOnce(properties: Record<string, unknown>): void {
        call((mp) => mp.people.set_once(properties));
    },
    startSessionRecording(): void {
        call((mp) => mp.start_session_recording());
    },
    stopSessionRecording(): void {
        call((mp) => mp.stop_session_recording());
    },
};
